/**
 * SharedItemPage — `/s/:shareId`
 *
 * Public, no-auth preview of an item a Reclaim member has chosen to
 * share. Renders the same "Session Recipe" card style as the share
 * modal, plus an inline play control for audios.
 *
 * Designed to convert curious visitors into members — every page ends
 * with a soft CTA into the Reclaim signup flow.
 */
import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Play, Pause, Sparkles } from 'lucide-react';
import { premiumPageBg, premiumHeading, premiumEyebrow, premiumDivider } from '../utils/premiumStyle';

const API = process.env.REACT_APP_BACKEND_URL;

const SharedItemPage = () => {
  const { shareId } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [playing, setPlaying] = useState(false);
  const audioRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    fetch(`${API}/api/share/${shareId}`)
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => { if (!cancelled) setData(d); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [shareId]);

  useEffect(() => {
    const tear = () => { const a = audioRef.current; if (a) { a.pause(); a.src = ''; } };
    window.addEventListener('pagehide', tear);
    return () => { window.removeEventListener('pagehide', tear); tear(); };
  }, []);

  const toggle = () => {
    if (!data?.audio_url) return;
    const a = audioRef.current;
    if (!a) return;
    if (playing) { a.pause(); setPlaying(false); return; }
    const src = data.audio_url.startsWith('http') ? data.audio_url : `${API}${data.audio_url}`;
    a.src = src; a.currentTime = 0; a.volume = 0.9;
    a.play().then(() => setPlaying(true)).catch(() => setPlaying(false));
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={premiumPageBg()}>
        <p className="text-white/55 text-sm">Loading…</p>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center px-6" style={premiumPageBg()}>
        <Sparkles size={32} className="text-amber-300/55 mb-3" />
        <p className="text-white/65 text-sm text-center">This share has been removed or never existed.</p>
        <button
          onClick={() => navigate('/')}
          className="mt-6 px-6 py-3 rounded-full text-[11px] tracking-[0.18em] uppercase"
          style={{ background: 'linear-gradient(180deg, #f1d29c 0%, #d9a55a 100%)', color: '#1f3a5c' }}
          data-testid="share-discover-cta"
        >
          Discover Reclaim
        </button>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col items-center px-6 py-10" style={premiumPageBg()} data-testid="shared-item-page">
      <audio ref={audioRef} onEnded={() => setPlaying(false)} />

      <p style={premiumEyebrow}>Shared with you</p>

      <motion.div
        initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="mt-6 rounded-3xl p-8 w-full max-w-sm"
        style={{
          background: 'linear-gradient(180deg, #1f3a5c 0%, #0c1a30 60%, #03060f 100%)',
          border: '1px solid rgba(241,210,156,0.32)',
          boxShadow: '0 24px 80px -20px rgba(0,0,0,0.85), 0 0 80px rgba(241,210,156,0.08)',
        }}
      >
        {data.image_url && (
          <img
            src={data.image_url}
            alt=""
            className="w-full aspect-square object-cover rounded-2xl"
            style={{ border: '1px solid rgba(241,210,156,0.18)' }}
          />
        )}
        <h1
          className="mt-6 text-center text-2xl text-white leading-snug"
          style={premiumHeading}
        >
          "{data.title}"
        </h1>
        <div style={{ ...premiumDivider(), marginTop: '20px', width: '60%', marginLeft: 'auto', marginRight: 'auto' }} aria-hidden />

        {data.audio_url && (
          <div className="mt-7 flex items-center justify-center">
            <button
              onClick={toggle}
              className="w-14 h-14 rounded-full flex items-center justify-center"
              style={{
                background: 'linear-gradient(180deg, #f1d29c 0%, #d9a55a 100%)',
                boxShadow: '0 12px 30px -16px rgba(217,165,90,0.7)',
              }}
              data-testid="share-play"
              aria-label={playing ? 'Pause' : 'Play'}
            >
              {playing
                ? <Pause size={20} className="text-[#1f3a5c]" />
                : <Play size={20} className="text-[#1f3a5c] ml-0.5" />}
            </button>
          </div>
        )}
        <p className="mt-7 text-center text-[10px] tracking-[0.4em] uppercase text-amber-200/75">Reclaim</p>
      </motion.div>

      <button
        onClick={() => navigate('/register')}
        className="mt-10 px-6 py-3 rounded-full text-[11px] tracking-[0.18em] uppercase"
        style={{ background: 'rgba(241,210,156,0.10)', border: '1px solid rgba(241,210,156,0.32)', color: 'rgba(241,210,156,0.95)' }}
        data-testid="share-discover-cta"
      >
        Discover Reclaim
      </button>
    </div>
  );
};

export default SharedItemPage;
